import { format, isValid } from 'date-fns';
import { Guide } from 'types/guide';
import { Surgerie } from 'types/surgerie';

export type RankingItem = {
  label: string;
  value: number;
  amount?: number;
};

export type TimelineItem = {
  date: string;
  amount: number;
  records: number;
};

export function buildRanking<T>(
  items: T[],
  getLabel: (item: T) => string,
  getAmount: (item: T) => number,
): RankingItem[] {
  const grouped = items.reduce<Record<string, RankingItem>>((acc, item) => {
    const label = getLabel(item);

    acc[label] = {
      label,
      value: (acc[label]?.value || 0) + 1,
      amount: (acc[label]?.amount || 0) + getAmount(item),
    };

    return acc;
  }, {});

  return Object.values(grouped)
    .sort((first, second) => second.value - first.value)
    .slice(0, 5);
}

export function getPercent(value: number, total: number) {
  if (!total) return 0;

  return Math.min((value / total) * 100, 100);
}

export function buildTimeline(guides: Guide[], surgeries: Surgerie[]): TimelineItem[] {
  const grouped = [...guides, ...surgeries].reduce<Record<string, TimelineItem>>((acc, item) => {
    const rawDate = 'value_doctor' in item ? item.closed_at || item.created_at : item.created_at;
    const parsedDate = new Date(rawDate);

    if (!isValid(parsedDate)) return acc;

    const key = format(parsedDate, 'yyyy-MM-dd');
    const label = format(parsedDate, 'dd/MM');

    acc[key] = {
      date: label,
      amount: (acc[key]?.amount || 0) + Number(item.value_total || 0),
      records: (acc[key]?.records || 0) + 1,
    };

    return acc;
  }, {});

  return Object.entries(grouped)
    .sort(([firstDate], [secondDate]) => firstDate.localeCompare(secondDate))
    .slice(-12)
    .map(([, item]) => item);
}
